import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { emails as api, type EmailQuery } from "@/api/endpoints";
import { EmptyState, Spinner } from "@/components/ui";
import Pagination from "@/components/ui/Pagination";
import EmailDetailModal from "@/features/emails/EmailDetailModal";
import { StatusBadge } from "@/features/emails/status";
import { formatDateTime } from "@/lib/utils";

/**
 * Tabuľka správ. To isté používa samostatná stránka aj detail klienta
 * a inštruktora — rozdiel je len v tom, čím je zoznam obmedzený (`filter`).
 *
 * Detail sa otvára kliknutím na riadok, nie tlačidlom: v tomto zozname je
 * jediná rozumná akcia práve „ukáž mi tú správu“.
 */
export default function EmailList({
  filter,
  pageSize: initialPageSize = 25,
  compact = false,
}: {
  filter?: EmailQuery;
  pageSize?: number;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [openId, setOpenId] = useState<number | null>(null);

  const params: EmailQuery = { ...filter, page, limit: pageSize };
  const { data, isLoading } = useQuery({
    queryKey: ["emails", params],
    queryFn: () => api.list(params),
    placeholderData: keepPreviousData,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const pages = Math.max(1, Math.ceil(count / pageSize));

  if (isLoading) {
    return (
      <div className="flex h-24 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  if (rows.length === 0) {
    return <EmptyState>{t("emails.empty")}</EmptyState>;
  }

  return (
    <>
      {/* Na mobile sa tabuľka posúva do strany - rodič ju inak oreže. */}
      <div className="overflow-x-auto">
        <table className="table-base">
          <thead>
            <tr>
              <th>{t("emails.queued")}</th>
              {compact ? null : <th>{t("emails.recipient")}</th>}
              <th>{t("emails.subject")}</th>
              <th>{t("emails.state")}</th>
              <th>{t("emails.delivered")}</th>
              {compact ? null : (
                <th className="text-right">{t("emails.attempts")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => setOpenId(row.id)}
              >
                <td className="whitespace-nowrap text-slate-500">
                  {formatDateTime(row.created)}
                </td>
                {compact ? null : (
                  <td>
                    <div>{row.to_name || row.to_email}</div>
                    {row.to_name ? (
                      <div className="text-xs text-slate-400">
                        {row.to_email}
                      </div>
                    ) : null}
                  </td>
                )}
                <td className="max-w-sm truncate">{row.subject}</td>
                <td>
                  <StatusBadge status={row.status} />
                  {/* Dôvod zlyhania patrí do zoznamu: bez neho by sa muselo
                    otvárať jedno po druhom, kým sa nájde to pravé. */}
                  {row.last_error ? (
                    <div className="max-w-xs truncate text-xs text-red-600">
                      {row.last_error}
                    </div>
                  ) : null}
                </td>
                <td className="whitespace-nowrap text-slate-500">
                  {formatDateTime(row.delivered_at) ||
                    formatDateTime(row.sent_at) ||
                    "—"}
                </td>
                {compact ? null : (
                  <td className="text-right tabular-nums text-slate-500">
                    {row.attempts > 1 ? `${row.attempts} ×` : ""}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pages={pages}
        pageSize={pageSize}
        count={count}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      {openId ? (
        <EmailDetailModal emailId={openId} onClose={() => setOpenId(null)} />
      ) : null}
    </>
  );
}
